const { getElement, getFocusableElements } = window.BAO.Utils
export class Drawer extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.drawers = getElement('site-drawers')
    if (!this.drawers && this.drawers.drawers) {
      throw new Error(
        `${this.nodeName.toLowerCase()} needs a <site-drawers> element to work`,
      )
    }
    this.drawers.drawers.push(this)

    this._isOpen = false
  }

  connectedCallback () {
    super.connectedCallback()

    if (this.drawerShouldBeMoved) {
      window.setTimeout(() => {
        const moveToElement = this.moveToElement

        this.removeAttribute('data-module-drawers-move-me')

        this._isMoving = true
        moveToElement.appendChild(this)
        this._isMoving = false
      }, parseInt(this.getAttribute('data-module-drawers-move-delay'), 10) || 0)
    }
  }

  disconnectedCallback () {
    if (this._isMoving) return

    super.disconnectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      this,
      'transitionend',
      this.handleTransitionEnd.bind(this),
    )

    this.listeners.add(document, 'keydown', this.handleKeyDown.bind(this))
  }

  handleTransitionEnd (event) {
    if (!event.target.matches(this.localName)) return
    if (event.propertyName !== 'visibility') return
    this.isVisible = getComputedStyle(this)?.visibility === 'visible'
  }

  handleKeyDown (event) {
    const KEYCODE_TAB = 9
    const isTab = event.code?.toUpperCase() === 'TAB' || event.keyCode === KEYCODE_TAB

    if (!isTab || !this.isActive) return

    const { firstFocusableEl, lastFocusableEl } = this

    if (!firstFocusableEl) {
      event.preventDefault()
      this.focus()
      return
    }

    if (!this.contains(document.activeElement)) {
      event.preventDefault()
      firstFocusableEl.focus()
    } else if (event.shiftKey && document.activeElement === firstFocusableEl) {
      event.preventDefault()
      lastFocusableEl.focus()
    } else if (!event.shiftKey && document.activeElement === lastFocusableEl) {
      event.preventDefault()
      firstFocusableEl.focus()
    }
  }

  /**
   * Moves focus into the drawer, falling back to the drawer itself when it has nothing focusable
   *
   * @returns {boolean} Whether focus is now inside the drawer
   */
  focusFirst () {
    if (this.firstFocusableEl) {
      this.firstFocusableEl.focus()
    } else {
      this.setAttribute('tabindex', '-1')
      this.focus()
    }

    return this.contains(document.activeElement)
  }

  get key () {
    // key is stripped by Tagalys
    return this.getAttribute('key') || this.getAttribute('data-key')
  }

  get elements () {
    return {
      trigger: getElement(`[data-drawers-trigger=${this.key}]`),
      close: getElement(`[data-module-drawers-close=${this.key}]`),
    }
  }

  get isVisible () {
    return this._isVisible
  }

  set isVisible (val) {
    this._isVisible = val
  }

  get isOpen () {
    return this._isOpen
  }

  get isActive () {
    return this.isOpen && this.key === this.drawers.activeDrawerKey
  }

  set isOpen (val) {
    this._isOpen = val

    this.classList.toggle(this.config.classes.activeDrawerClass, val)
  }

  get drawerShouldBeMoved () {
    return this.hasAttribute('data-module-drawers-move-me')
  }

  get moveToElement () {
    switch (this.getAttribute('data-module-drawers-move-me')) {
      case 'root':
        return this.drawers
      default:
        return getElement(this.getAttribute('data-module-drawers-move-me'))
    }
  }

  get config () {
    return {
      classes: {
        activeDrawerClass: 'drw-Drawer-active',
      },
    }
  }

  get focusableEls () {
    return getFocusableElements(this).filter(
      (el) =>
        el.getClientRects().length > 0 &&
        getComputedStyle(el).visibility !== 'hidden' &&
        !el.closest('[aria-hidden="true"], [inert]'),
    )
  }

  get firstFocusableEl () {
    return this.focusableEls?.[0]
  }

  get lastFocusableEl () {
    return this.focusableEls?.[this.focusableEls.length - 1]
  }
}

if (!customElements.get('site-drawer')) {
  customElements.define('site-drawer', Drawer)
}
